// Your connected Google account, in Settings › Account: connect one (a Google sign-in proves it's yours),
// make it primary so Rolodex emails go there, switch or disconnect it. Administration › Users has the same
// for anyone (pages/admin/Users.tsx).
import { useEffect, useId, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { unlinkLeavesNoSignIn, type LinkedAccount } from '@technohull/shared';
import { GOOGLE_CONNECT_URL, useMyGoogle, useMyGoogleDisconnect, useMyGooglePrimary } from '../features/account.js';
import { InlineError, LogoSpin, useConfirm } from './ui.js';

/** Google's "G", for the buttons and rows that are about a Google account. */
export function GoogleMark({ size = 16 }: { size?: number }) {
  return (
    <svg className="g-mark" width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/** the address, its Primary / not-confirmed tags. */
export function GoogleAccountLine({ account }: { account: LinkedAccount }) {
  return (
    <div className="g-acct">
      <GoogleMark size={18} />
      <span className="g-acct-email">{account.email}</span>
      {account.primary && <span className="pill ok">Primary</span>}
      {!account.verified && <span className="pill">Not confirmed yet</span>}
    </div>
  );
}

/** what the connection does for this account, in a sentence or two. `who` is "you" or the person's name. */
export function googleAccountHint(account: LinkedAccount, rolodexEmail: string, who: 'you' | string): string {
  const same = account.email.toLowerCase() === rolodexEmail.toLowerCase();
  const you = who === 'you';
  const signIn = same
    ? `Sign in with Google opens this account.`
    : `Signing in with Google as ${account.email} opens this account.`;
  const emails = you ? 'your Rolodex emails' : `Rolodex emails for ${who}`;
  const mail = account.primary
    ? `It's primary: ${emails} go to ${account.email}.`
    : same
      ? `It's the same address, so ${emails} go there either way.`
      : `Rolodex emails go to ${rolodexEmail}. Make the Google account primary to send them to ${account.email} instead.`;
  const confirm = account.verified ? '' : ` Connected by an administrator: it's confirmed the first time ${you ? 'you sign' : 'they sign'} in with it.`;
  return `${signIn} ${mail}${confirm}`;
}

const RETURN_COPY: Record<string, string> = { connected: 'Google account connected.' };

export function MyGoogleAccount({ rolodexEmail }: { rolodexEmail: string }) {
  const q = useMyGoogle();
  const primary = useMyGooglePrimary();
  const unlink = useMyGoogleDisconnect();
  const [confirmNode, confirm] = useConfirm();
  const [params, setParams] = useSearchParams();
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const id = useId();

  // back from Google (?google=connected or ?googleError=…): say how it went, then tidy the address bar
  useEffect(() => {
    const ok = params.get('google');
    const err = params.get('googleError');
    if (!ok && !err) return;
    setNotice(err ? { ok: false, text: err } : { ok: true, text: RETURN_COPY[ok ?? ''] ?? 'Done.' });
    const next = new URLSearchParams(params);
    next.delete('google');
    next.delete('googleError');
    setParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const data = q.data;
  const acct = data?.account ?? null;
  const busy = primary.isPending || unlink.isPending;
  const locked = !!acct && !!data && unlinkLeavesNoSignIn(rolodexEmail, acct.email, data.hasPassword);

  const makePrimary = (v: boolean) => {
    setNotice(null);
    primary.mutate(v, {
      onSuccess: () => setNotice({ ok: true, text: v ? 'Your Google account is now primary.' : 'Your Rolodex address is primary again.' }),
    });
  };

  const onDisconnect = async () => {
    if (!acct) return;
    const same = acct.email.toLowerCase() === rolodexEmail.toLowerCase();
    const ok = await confirm({
      title: 'Disconnect Google account',
      message:
        `Disconnect ${acct.email}? ` +
        (same ? 'You can still sign in with Google as your Rolodex address.' : `Signing in with Google as ${acct.email} will no longer open this account.`) +
        (acct.primary ? ` Your Rolodex emails go back to ${rolodexEmail}.` : ''),
      confirmLabel: 'Disconnect',
      danger: true,
    });
    if (!ok) return;
    setNotice(null);
    unlink.mutate(undefined, { onSuccess: () => setNotice({ ok: true, text: 'Google account disconnected.' }) });
  };

  return (
    <div className="settings-row g-row" role="group" aria-labelledby={`${id}-g`}>
      <p className="settings-label" id={`${id}-g`}>Google account</p>
      {q.isLoading ? (
        <p className="settings-hint"><LogoSpin size={14} decorative /> Checking…</p>
      ) : q.error ? (
        <InlineError error={q.error} />
      ) : acct ? (
        <>
          <GoogleAccountLine account={acct} />
          <p className="settings-hint">{googleAccountHint(acct, rolodexEmail, 'you')}</p>
          {locked && (
            <p className="settings-hint">It's how you sign in and your account has no password, so it can't be disconnected until an administrator sets one.</p>
          )}
          <div className="settings-account-actions">
            {acct.primary ? (
              <button className="btn" disabled={busy} onClick={() => makePrimary(false)}>Make Rolodex address primary</button>
            ) : (
              <button className="btn btn-primary" disabled={busy} onClick={() => makePrimary(true)}>Make primary</button>
            )}
            {data?.configured && (
              <a className="btn" href={GOOGLE_CONNECT_URL}>{acct.verified ? 'Use another Google account' : 'Confirm with Google'}</a>
            )}
            {!locked && <button className="btn btn-ghost" disabled={busy} onClick={() => void onDisconnect()}>Disconnect</button>}
          </div>
        </>
      ) : data?.configured ? (
        <>
          <p className="settings-hint">Connect your Google account to sign in with Google. You can then make it primary to get your Rolodex emails there.</p>
          <div className="settings-account-actions">
            <a className="btn" href={GOOGLE_CONNECT_URL}><GoogleMark /> Connect Google account</a>
          </div>
        </>
      ) : (
        <p className="settings-hint">Google sign-in isn't set up for Rolodex yet, so there's nothing to connect for now.</p>
      )}
      {notice && (notice.ok
        ? <p className="settings-hint g-note" role="status">{notice.text}</p>
        : <InlineError error={new Error(notice.text)} onDismiss={() => setNotice(null)} />)}
      {primary.isError && <InlineError error={primary.error} onDismiss={() => primary.reset()} />}
      {unlink.isError && <InlineError error={unlink.error} onDismiss={() => unlink.reset()} />}
      {confirmNode}
    </div>
  );
}
